import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Renderer2, ViewChild, inject , OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouterModule , Router } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { filter } from 'rxjs';

import { AppFooter } from './app.footer';
import { AppHeader } from './app.header';
import { AppSidebar } from './app.sidebar';
import { ResourceNotFoundDialogComponent } from '../../shared/components/resource-not-found-dialog/resource-not-found-dialog.component';
import { LoadingService } from '../../shared/services/loading.service';
import { SafeAny } from '../../shared/types/safe-any';
import { LayoutService } from '../service/layout.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppSidebar,
    RouterModule,
    AppFooter,
    ToastModule,
    ProgressSpinnerModule,
    AppHeader,
    ResourceNotFoundDialogComponent,
  ],
  template: ` @if (loadingService.isLoading()) {
      <div
        class="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center"
        style="z-index: 999;"
      >
        <p-progress-spinner strokeWidth="5" animationDuration="2s"></p-progress-spinner>
      </div>
    }
    <app-resource-not-found-dialog />
    <div class="layout-wrapper" [ngClass]="containerClass">
      <app-sidebar></app-sidebar>

      <div class="layout-main-container">
        <div class="layout-main">
          <app-header></app-header>
          <router-outlet></router-outlet>
        </div>
        <app-footer></app-footer>
      </div>
      <div class="layout-mask animate-fadein"></div>
      <p-toast position="bottom-center"></p-toast>
    </div>`,
  styles: [
    `
      :host ::ng-deep .p-progressspinner .p-progressspinner-circle {
        stroke: var(--primary-color) !important;
      }
    `,
  ],
})
export class AppLayout implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  menuOutsideClickListener: SafeAny;

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    public loadingService: LoadingService
  ) {
    this.layoutService.overlayOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.menuOutsideClickListener ??= this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });

        if (this.layoutService.layoutState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hideMenu();
      });
  }

  isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    const eventTarget = event.target as Node;

    return !(
      sidebarEl?.isSameNode(eventTarget) ||
      sidebarEl?.contains(eventTarget) ||
      topbarEl?.isSameNode(eventTarget) ||
      topbarEl?.contains(eventTarget)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  get containerClass() {
    return {
      'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
      'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
      'layout-static-inactive':
        this.layoutService.layoutState().staticMenuDesktopInactive &&
        this.layoutService.layoutConfig().menuMode === 'static' &&
        this.layoutService.isDesktop(),
      'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
      'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
    };
  }

  ngOnDestroy() {
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
